import React, { useEffect, useState } from "react";
import { adminLogin, fetchLeads, updateLeadStatus } from "../lib/api";
import { TactiqLogo } from "../components/brand/TactiqAssets";
import { Lock, RefreshCw, LogOut, ArrowLeft, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  quoted: "bg-purple-100 text-purple-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-slate-100 text-slate-600",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("tq_admin_token"));
  const [pwd, setPwd] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const data = await fetchLeads(t);
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("tq_admin_token");
        setToken(null);
        toast.error("Session expired. Sign in again.");
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      const r = await adminLogin(pwd);
      localStorage.setItem("tq_admin_token", r.token);
      setToken(r.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Invalid password.");
    }
  };

  const logout = () => {
    localStorage.removeItem("tq_admin_token");
    setToken(null);
    setLeads([]);
  };

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status, token);
      setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--tq-bg-3)] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md w-full">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-8"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
          <TactiqLogo withTag />
          <h1 className="font-display text-3xl font-black tracking-tighter mt-8">
            Studio Login
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Access incoming quote requests and lead pipeline.
          </p>
          <form onSubmit={onLogin} className="mt-8 space-y-4">
            <label className="block">
              <span className="overline">Password</span>
              <div className="relative mt-3">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  data-testid="admin-password"
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:border-[var(--tq-navy)] outline-none"
                />
              </div>
            </label>
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="tq-btn tq-btn-primary w-full justify-center"
            >
              Sign in
            </button>
          </form>
          <p className="text-xs text-slate-400 mt-6 text-center font-mono">
            Default: tactiq2026
          </p>
        </div>
      </div>
    );
  }

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = leads.reduce((acc, l) => {
    acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[var(--tq-bg-3)]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo">
            <TactiqLogo />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load()}
              data-testid="admin-refresh"
              className="tq-btn tq-btn-ghost"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="tq-btn tq-btn-ghost"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline mb-2">Lead Pipeline</div>
            <h1 className="font-display text-4xl font-black tracking-tighter">
              Quote Requests
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {["all", "new", "contacted", "quoted", "won", "lost"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`admin-filter-${f}`}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                  filter === f
                    ? "bg-[var(--tq-navy)] text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {f} {f !== "all" && counts[f] ? `(${counts[f]})` : ""}
                {f === "all" && leads.length ? `(${leads.length})` : ""}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div
            className="bg-white rounded-2xl border border-slate-200 p-16 text-center"
            data-testid="admin-empty"
          >
            <p className="text-slate-500">No quote requests yet. They&apos;ll show up here.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-leads-list">
            {filtered.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-${l.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-display text-xl font-bold tracking-tight">
                        {l.name}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${
                          STATUS_COLORS[l.status] || "bg-slate-100"
                        }`}
                      >
                        {l.status}
                      </span>
                    </div>
                    {l.company && (
                      <div className="text-sm text-slate-600">{l.company}</div>
                    )}
                  </div>
                  <div className="flex flex-col sm:items-end gap-1 text-sm">
                    <a
                      href={`mailto:${l.email}`}
                      className="inline-flex items-center gap-2 text-slate-700 hover:text-[var(--tq-navy)]"
                    >
                      <Mail size={14} /> {l.email}
                    </a>
                    {l.phone && (
                      <a
                        href={`tel:${l.phone}`}
                        className="inline-flex items-center gap-2 text-slate-700 hover:text-[var(--tq-navy)]"
                      >
                        <Phone size={14} /> {l.phone}
                      </a>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                  {l.industry && (
                    <div>
                      <div className="overline text-[10px] mb-1">Industry</div>
                      <div className="text-slate-800">{l.industry}</div>
                    </div>
                  )}
                  {l.quantity && (
                    <div>
                      <div className="overline text-[10px] mb-1">Quantity</div>
                      <div className="text-slate-800">{l.quantity}</div>
                    </div>
                  )}
                  {l.use_case && (
                    <div>
                      <div className="overline text-[10px] mb-1">Use case</div>
                      <div className="text-slate-800">{l.use_case}</div>
                    </div>
                  )}
                </div>

                {l.message && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-xl text-sm text-slate-700">
                    &ldquo;{l.message}&rdquo;
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-400 font-mono">
                    {new Date(l.created_at).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {["new", "contacted", "quoted", "won", "lost"].map((s) => (
                      <button
                        key={s}
                        onClick={() => changeStatus(l.id, s)}
                        data-testid={`lead-status-${l.id}-${s}`}
                        className={`text-[10px] uppercase font-mono tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                          l.status === s
                            ? "bg-[var(--tq-navy)] text-white"
                            : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
